// MCPipelinesPage — Reforma UX §6.8 / wireframe "09 Pipelines — Ativos".
//
// Registry de pipelines em CARDS (padrão de Skills):
//   - card: switch ativar/desativar (PATCH /v1/pipelines/:id, otimista com
//     revert graceful §7.6) · versão · scope · kebab ⋮ (ver detalhes ·
//     promover/instanciar · excluir[gap]) · métricas runs · % sucesso · etapas.
//   - PIPE-01: SEM badge "requires business" e SEM diagrama DAG no card.
//   - PIPE-05: % sucesso = completos/(completos+falhos), canceladas FORA —
//     calculado de listPipelineRuns (lib/pipeline-counts + pipeline-metrics).
//   - Filtros: scope · status (client-side).
//   - "Novo pipeline" via CreateAssetModal type=pipeline (escopo livre).
//   - Detalhe vira MODAL (components/pipelines/PipelineDetailModal) com abas
//     Etapas · Versões · Runs (demandas bloqueadas + runs) · Métricas.
//   - Deep-link: /pipelines/:id renderiza esta page com a modal aberta
//     (prop deepLinkPipelineId — ver PipelineDetailPage wrapper).
//
// Nota wireframe: o card NÃO tem mais botão "Kanban"; a rota
// /pipelines/:id/board segue viva por URL direta (princípio §2.6 — sem
// deletar rota viva). PipelineBoardPage não muda.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/layout/PageHeader';
import { Plus, Workflow, Globe, Copy } from 'lucide-react';
import '@/styles/wf-agents.css'; // chrome compartilhado: .dhead/.dfilters/.dnewbtn + .agt-sw
import '@/styles/wf-skills.css'; // anatomia dos cards (.skl-grid/.sklc)
import '@/styles/wf-pipes.css';
import { CreateAssetModal } from '@/components/CreateAssetModal';
import { PipelineDetailModal } from '@/components/pipelines/PipelineDetailModal';
import { PipelineCard } from '@/components/pipelines/PipelineCard';
import { countSteps } from '@/components/pipelines/pipeline-metrics';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import { buildPromoteFeedback, getPromoteCopy } from '@/lib/promote-feedback';
import { useSupabaseRealtime } from '@/hooks/useSupabaseRealtime';
import {
  listPipelines,
  listPipelineRuns,
  getPipeline,
  patchPipeline,
  instantiateGlobalPipeline,
  promoteTenantPipeline,
  type PipelineSummary,
} from '@/lib/api-pipelines';
import { aggregateRuns, type RunCounts } from '@/lib/pipeline-counts';

const SCOPE_OPTIONS = [
  { value: '', label: 'Todos os scopes' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'global', label: 'Global' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'active', label: 'Ativos' },
  { value: 'inactive', label: 'Inativos' },
];

export interface MCPipelinesPageProps {
  /** Deep-link /pipelines/:id — abre a modal de detalhe sobre a lista. */
  deepLinkPipelineId?: string | null;
}

export function MCPipelinesPage({ deepLinkPipelineId = null }: MCPipelinesPageProps) {
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState<PipelineSummary[]>([]);
  const [runCounts, setRunCounts] = useState<Map<string, RunCounts>>(new Map());
  // Nº de etapas por pipeline (versão ativa). GAP menor reportado: o GET
  // /v1/pipelines (lista) não retorna steps_count — derivamos com fetch lazy
  // do detalhe de cada pipeline (best-effort; "—" quando falha).
  const [stepsCounts, setStepsCounts] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [scopeFilter, setScopeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailPipelineId, setDetailPipelineId] = useState<string | null>(deepLinkPipelineId);
  const [promoteTarget, setPromoteTarget] = useState<PipelineSummary | null>(null);
  const [instantiateTarget, setInstantiateTarget] = useState<PipelineSummary | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const flashToast = useCallback((msg: string, ms = 8000) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), ms);
  }, []);

  const loadStepsCounts = useCallback(async (pls: PipelineSummary[]) => {
    // Best-effort N+1 (lista é pequena); cada falha vira "—" sem derrubar a page.
    const results = await Promise.allSettled(pls.map((p) => getPipeline(p.id)));
    const map = new Map<string, number>();
    results.forEach((res, i) => {
      if (res.status === 'fulfilled') {
        const n = countSteps(res.value.versions, res.value.latest_version_id);
        if (n !== null) map.set(pls[i].id, n);
      }
    });
    setStepsCounts(map);
  }, []);

  const reload = useCallback(() => {
    // light: true — não baixa a coluna `config` dos runs (o grosso do payload:
    // ~170 runs / 2.6MB / 2s por load e por evento realtime — pipeline.routes.ts:855-857).
    // A agregação de contadores (aggregateRuns) só lê status/tasks_*; nenhum consumidor
    // de runs na listagem lê `.config`, então o modo light é zero-regressão aqui.
    Promise.all([listPipelines(), listPipelineRuns({ light: true })])
      .then(([pls, runs]) => {
        setPipelines(pls);
        setRunCounts(aggregateRuns(runs));
        setError(null);
        void loadStepsCounts(pls);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [loadStepsCounts]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Realtime: pipelines + runs (contadores dos cards atualizam sem reload).
  useSupabaseRealtime({ table: 'pipeline_runs' }, reload);
  useSupabaseRealtime({ table: 'pipelines' }, reload);

  // Switch ativar/desativar — OTIMISTA: UI muda na hora, PATCH em seguida;
  // erro → revert + toast graceful (§7.6: endpoint ativa pós-restart do backend).
  const handleToggleActive = async (p: PipelineSummary, next: boolean) => {
    setPipelines((cur) => cur.map((x) => (x.id === p.id ? { ...x, is_active: next } : x)));
    try {
      await patchPipeline(p.id, { is_active: next });
    } catch (err) {
      setPipelines((cur) => cur.map((x) => (x.id === p.id ? { ...x, is_active: p.is_active } : x)));
      flashToast(
        `Não foi possível ${next ? 'ativar' : 'desativar'} ${p.slug}: `
        + `${err instanceof Error ? err.message : 'erro desconhecido'}. `
        + '(PATCH /v1/pipelines/:id pode ainda não estar ativo no backend.)',
      );
    }
  };

  const confirmPromote = async () => {
    if (!promoteTarget) return;
    // Backend: POST /v1/pipelines/:id/promote — tool promote_asset em cascata.
    const result = await promoteTenantPipeline(promoteTarget.id);
    flashToast(
      buildPromoteFeedback(
        'pipeline',
        promoteTarget.name || promoteTarget.slug || promoteTarget.id,
        result,
      ),
    );
    reload();
  };

  const confirmInstantiate = async () => {
    if (!instantiateTarget) return;
    // Backend: POST /v1/pipelines/:id/instantiate — clona global → tenant.
    const res = await instantiateGlobalPipeline(instantiateTarget.id);
    flashToast(`Pipeline ${instantiateTarget.slug} instanciado no tenant.`);
    reload();
    setDetailPipelineId(res.pipeline_id);
  };

  const filtered = useMemo(() => pipelines.filter((p) => {
    if (scopeFilter && p.scope_label !== scopeFilter) return false;
    if (statusFilter === 'active' && !p.is_active) return false;
    if (statusFilter === 'inactive' && p.is_active) return false;
    return true;
  }), [pipelines, scopeFilter, statusFilter]);

  const closeDetail = () => {
    setDetailPipelineId(null);
    // Deep-link (/pipelines/:id): fechar a modal volta pra lista canônica.
    if (deepLinkPipelineId) navigate('/pipelines', { replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pipelines"
        description="Registry de pipelines — configure as etapas e acompanhe as runs."
      />

      {/* Header de ação + filtros (wireframe §09 .dhead: botão + .dfilters) */}
      <div className="dhead">
        <button type="button" className="dnewbtn" onClick={() => setCreateModalOpen(true)}>
          <Plus className="ic" aria-hidden />
          Novo pipeline
        </button>
        <div className="dfilters">
          <select
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value)}
            aria-label="Filtro de scope"
          >
            {SCOPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filtro de status"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* F8 — modal de criação por ESCOPO LIVRE (gera PIPELINE.md + steps) */}
      <CreateAssetModal
        type="pipeline"
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmitted={(msg) => flashToast(msg, 7000)}
      />

      {error && (
        <div className="rounded-lg border border-raios-danger/30 bg-raios-danger/10 p-4 text-sm text-raios-danger-text">
          Erro ao carregar pipelines: {error}
        </div>
      )}

      {loading ? (
        <div className="skl-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-52 w-full rounded-lg" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Workflow}
          title={pipelines.length === 0 ? 'Nenhum pipeline ainda' : 'Nenhum pipeline com esses filtros'}
          description={
            pipelines.length === 0
              ? 'Crie seu primeiro pipeline pra começar a processar demandas.'
              : 'Ajuste os filtros de scope/status pra ver os pipelines.'
          }
          action={{ label: 'Novo pipeline', onClick: () => setCreateModalOpen(true) }}
        />
      ) : (
        <div className="skl-grid">
          {filtered.map((p) => (
            <PipelineCard
              key={p.id}
              pipeline={p}
              counts={runCounts.get(p.id)}
              stepsCount={stepsCounts.get(p.id) ?? null}
              onOpenDetail={() => setDetailPipelineId(p.id)}
              onToggleActive={(next) => void handleToggleActive(p, next)}
              onPromote={() => setPromoteTarget(p)}
              onInstantiate={() => setInstantiateTarget(p)}
            />
          ))}
        </div>
      )}

      {/* Modal de detalhe sobre a lista — abas Etapas/Versões/Runs/Métricas */}
      <PipelineDetailModal
        pipelineIdOrSlug={detailPipelineId}
        open={detailPipelineId !== null}
        onClose={closeDetail}
        onChanged={reload}
      />

      {/* Promover tenant → global (confirmação obrigatória) */}
      {promoteTarget && (
        <ConfirmActionDialog
          open={!!promoteTarget}
          onOpenChange={(o) => { if (!o) setPromoteTarget(null); }}
          title={getPromoteCopy('pipeline').title}
          description={getPromoteCopy('pipeline').description}
          confirmLabel={getPromoteCopy('pipeline').confirmLabel}
          confirmVariant="brand"
          confirmIcon={Globe}
          onConfirm={confirmPromote}
        >
          <p className="text-sm text-raios-text-secondary">
            Pipeline: <span className="font-mono text-raios-text">{promoteTarget.slug}</span>
            {promoteTarget.name && ` — ${promoteTarget.name}`}
          </p>
        </ConfirmActionDialog>
      )}

      {/* Instanciar global → tenant (confirmação obrigatória) */}
      {instantiateTarget && (
        <ConfirmActionDialog
          open={!!instantiateTarget}
          onOpenChange={(o) => { if (!o) setInstantiateTarget(null); }}
          title="Instanciar pipeline no seu tenant?"
          description="Cria uma cópia deste pipeline global no seu tenant pra customização. O original continua disponível."
          confirmLabel="Instanciar"
          confirmVariant="brand"
          confirmIcon={Copy}
          onConfirm={confirmInstantiate}
        >
          <p className="text-sm text-raios-text-secondary">
            Pipeline: <span className="font-mono text-raios-text">{instantiateTarget.slug}</span>
            {instantiateTarget.name && ` — ${instantiateTarget.name}`}
          </p>
        </ConfirmActionDialog>
      )}

      {/* Toast inline simples */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 max-w-md rounded-lg border border-raios-border bg-raios-elevated px-4 py-3 shadow-raios-lg text-sm text-raios-text">
          {toast}
        </div>
      )}
    </div>
  );
}
